import { useEffect, type RefObject } from 'react';

/** The widget root, or a stable ref to it (read when the effect runs). */
export type HostIsolationRoot = HTMLElement | null | RefObject<HTMLElement | null>;

// Body children that never render; nothing to hide there.
const NON_RENDERED = /^(?:SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/;
// Inline body styles touched by the scroll lock. The overflow longhands are recorded too so
// a host that only set `overflow-y` gets exactly that back.
const LOCKED_STYLES = ['position', 'top', 'width', 'overflow', 'overflow-x', 'overflow-y'];

// Page-wide state shared by every widget instance on the page.
/** Body-level mount nodes containing an active modal widget, with how many are active. */
const hosts = new Map<Element, number>();
/** Siblings this module isolated, with their aria-hidden value from before. */
const isolated = new Map<Element, string | null>();
let bodyLock: { scrollY: number; hadStyle: boolean; saved: string[][] } | null = null;
let observer: MutationObserver | null = null;

/** The child of <body> that contains `el`, crossing shadow roots; null when detached. */
function findHost(el: Element): Element | null {
  let node: Element | null = el;
  while (node && node.parentNode !== document.body) {
    node = node.parentElement ?? (node.getRootNode() as ShadowRoot).host ?? null;
  }
  return node;
}

/** Make every body child outside an active host inert; undo it for everything else. */
function reconcile(): void {
  const keep = new Set<Element>();
  if (hosts.size > 0) {
    for (const child of Array.from(document.body.children)) {
      if (hosts.has(child) || NON_RENDERED.test(child.tagName)) continue;
      if (!isolated.has(child)) {
        // Already hidden by the host page: leave it exactly as it is.
        if (child.hasAttribute('inert') || child.getAttribute('aria-hidden') === 'true') continue;
        isolated.set(child, child.getAttribute('aria-hidden'));
        child.setAttribute('inert', '');
        // Fallback for browsers without `inert`.
        child.setAttribute('aria-hidden', 'true');
      }
      keep.add(child);
    }
  }
  isolated.forEach((ariaHidden, el) => {
    if (keep.has(el)) return;
    isolated.delete(el);
    el.removeAttribute('inert');
    if (ariaHidden === null) el.removeAttribute('aria-hidden');
    else el.setAttribute('aria-hidden', ariaHidden);
  });
}

function lockBodyScroll(): void {
  const { body } = document;
  const { style } = body;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  bodyLock = {
    scrollY,
    hadStyle: body.hasAttribute('style'),
    saved: LOCKED_STYLES.map(p => [p, style.getPropertyValue(p), style.getPropertyPriority(p)]),
  };
  // Fixed + negative top keeps the page visually in place (no jump) while it cannot scroll.
  style.setProperty('position', 'fixed', 'important');
  style.setProperty('top', `${-scrollY}px`, 'important');
  style.setProperty('width', '100%', 'important');
  style.setProperty('overflow', 'hidden', 'important');
}

function unlockBodyScroll(): void {
  if (!bodyLock) return;
  const { body } = document;
  const { style } = body;
  const { scrollY, hadStyle, saved } = bodyLock;
  bodyLock = null;
  saved.forEach(([p]) => style.removeProperty(p));
  saved.forEach(([p, value, priority]) => {
    if (value) style.setProperty(p, value, priority);
  });
  if (!hadStyle && style.length === 0) body.removeAttribute('style');
  if (!scrollY) return;
  try {
    // `instant` so a host `scroll-behavior: smooth` does not animate from the top.
    window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
  } catch {
    window.scrollTo(0, scrollY);
  }
}

function acquire(host: Element): void {
  hosts.set(host, (hosts.get(host) ?? 0) + 1);
  if (!bodyLock) lockBodyScroll();
  if (!observer && typeof MutationObserver === 'function') {
    // Content the host page adds while the modal is open is isolated too.
    observer = new MutationObserver(reconcile);
    observer.observe(document.body, { childList: true });
  }
  reconcile();
}

function release(host: Element): void {
  const count = (hosts.get(host) ?? 0) - 1;
  if (count > 0) hosts.set(host, count);
  else hosts.delete(host);
  reconcile();
  if (hosts.size > 0) return;
  observer?.disconnect();
  observer = null;
  unlockBodyScroll();
}

/**
 * While `active`, treats the widget as a true modal: every other child of `<body>` gets
 * `inert` + `aria-hidden="true"` and body scroll is locked in place. Everything is restored
 * to its previous value when `active` turns false or the component unmounts. Reference
 * counted across widget instances; a no-op without a DOM or a mounted root.
 */
export function useHostIsolation(active: boolean, rootEl: HostIsolationRoot): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined' || !document.body) return undefined;
    const el = rootEl && 'current' in rootEl ? rootEl.current : rootEl;
    const host = el ? findHost(el) : null;
    if (!host) return undefined;
    acquire(host);
    return () => release(host);
  }, [active, rootEl]);
}
